import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X } from "lucide-react";
import { products } from "../../data/products";
import { formatPrice } from "../../lib/utils";
import { useStore } from "../../context/StoreProvider";

// Frontend-only search: matches product name, category & collection.
const SearchOverlay = () => {
  const { searchOpen, setSearchOpen } = useStore();
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchOpen) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 60);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const onKey = (e) => e.key === "Escape" && setSearchOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOpen, setSearchOpen]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => {
      return (
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.collection.toLowerCase().includes(q) ||
        p.fit.toLowerCase().includes(q) ||
        p.colors.some((c) => c.name.toLowerCase().includes(q))
      );
    });
  }, [query]);

  const go = (slug) => {
    setSearchOpen(false);
    navigate(`/product/${slug}`);
  };

  if (!searchOpen) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-paper animate-fade-in" data-testid="search-overlay">
      <div className="container-9one py-6">
        <div className="flex items-center gap-4 border-b border-ink pb-4">
          <SearchIcon size={24} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, styles…"
            data-testid="search-input"
            className="w-full bg-transparent font-display text-3xl uppercase tracking-tight placeholder:text-ink/30 focus:outline-none"
          />
          <button
            onClick={() => setSearchOpen(false)}
            aria-label="Close search"
            data-testid="search-close-btn"
            className="hover:text-brand-dark transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        <div className="mt-6 max-h-[70vh] overflow-y-auto no-scrollbar">
          {query.trim() && results.length === 0 && (
            <p className="py-16 text-center label-mono text-ink/50" data-testid="search-no-results">
              No results for “{query}”
            </p>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {results.map((p) => (
              <button
                key={p.id}
                onClick={() => go(p.slug)}
                className="group text-left"
                data-testid={`search-result-${p.slug}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-cream">
                  <img
                    src={p.images[0]}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <p className="mt-2 text-sm font-medium">{p.name}</p>
                <p className="font-mono text-sm text-ink/60">{formatPrice(p.price)}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
